/**
 * View Filter Toggle
 *
 * Positions a sliding indicator behind the active option
 * and toggles aria-pressed on click.
 *
 * @package oska
 */

export default function initViewFilter() {
  document.querySelectorAll("[data-view-filter]").forEach((filter) => {
    const indicator = filter.querySelector("[data-view-filter-indicator]");
    const options = [...filter.querySelectorAll("[data-view-filter-option]")];
    const sectorOnlyElements = document.querySelectorAll(
      "[data-sector-view-only]",
    );

    if (!indicator || options.length === 0) return;

    function updateIndicator(animate = true) {
      const active = filter.querySelector(
        '[data-view-filter-option][aria-pressed="true"]',
      );
      if (!active) return;

      if (!animate) {
        indicator.style.transition = "none";
      }

      indicator.style.left = active.offsetLeft + "px";
      indicator.style.width = active.offsetWidth + "px";

      if (!animate) {
        indicator.offsetHeight; // force reflow
        indicator.style.transition = "";
      }
    }

    /**
     * Update visibility of sector-view-only elements
     * @param {number} activeIndex - 0 for sector view, 1 for party view
     */
    function updateSectorViewElements(activeIndex) {
      const isSectorView = activeIndex === 0;

      sectorOnlyElements.forEach((el) => {
        if (isSectorView) {
          el.removeAttribute("hidden");
        } else {
          el.setAttribute("hidden", "");
          // Clear any highlight when switching away from sector view
          document.dispatchEvent(
            new CustomEvent("scrolling-cards:clear-highlight"),
          );
        }
      });
    }

    options.forEach((option, idx) => {
      option.addEventListener("click", () => {
        if (option.getAttribute("aria-pressed") === "true") return;

        options.forEach((o) => o.setAttribute("aria-pressed", "false"));
        option.setAttribute("aria-pressed", "true");
        updateIndicator();
        updateSectorViewElements(idx);

        filter.dispatchEvent(
          new CustomEvent("view-filter:change", {
            bubbles: true,
            detail: { index: idx },
          }),
        );
      });
    });

    // Listen for external view changes (e.g., from URL navigation)
    document.addEventListener("view-filter:set", (e) => {
      const targetIndex = e.detail?.index;
      if (
        typeof targetIndex !== "number" ||
        targetIndex < 0 ||
        targetIndex >= options.length
      )
        return;

      const targetOption = options[targetIndex];
      if (!targetOption || targetOption.getAttribute("aria-pressed") === "true")
        return;

      // Update UI state
      options.forEach((o) => o.setAttribute("aria-pressed", "false"));
      targetOption.setAttribute("aria-pressed", "true");
      updateIndicator();
      updateSectorViewElements(targetIndex);
    });

    // Reposition indicator on resize
    window.addEventListener("resize", () => updateIndicator(false));

    // Initialize indicator and sector view visibility
    updateIndicator(false);
    const initialActiveIndex = options.findIndex(
      (o) => o.getAttribute("aria-pressed") === "true",
    );
    updateSectorViewElements(initialActiveIndex >= 0 ? initialActiveIndex : 0);
  });
}
